import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { DialogService } from 'primeng/dynamicdialog';
import { RatingModule } from 'primeng/rating';
import { TagModule } from 'primeng/tag';
import { ProductStockForm } from '../product-stock-form/product-stock-form';
import {EbayService, ProductService, StockService, UtilityService} from '@app/core/services';
import { BaseComponent } from '@app/core/bases';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import {Product, Stock, StockPriceHistory} from '@app/core/interfaces';
import { ConfirmationService, MenuItem, PrimeIcons } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TabStates } from '../product-detail/product-detail';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { Nl2brPipe } from '@app/core/pipes';
import {ProductStockChart} from "@app/features/product/product-stock-chart/product-stock-chart";
import {EbayProductAnalysis} from "@app/core/interfaces/ebay";

@Component({
  selector: 'app-product-detail-stocks',
  imports: [CommonModule, FormsModule, CardModule, TagModule, RatingModule, ChipModule, ButtonModule, MenuModule, ConfirmDialogModule, ProgressSpinnerModule, PopoverModule, TooltipModule, Nl2brPipe],
  providers: [ConfirmationService, DialogService],
  templateUrl: './product-detail-stocks.html'
})
export class ProductDetailStocks extends BaseComponent implements OnInit, OnChanges {
  @Input() product!: Product;
  @Output() productChange: EventEmitter<Product> = new EventEmitter<Product>();

  @Input() tabStates!: TabStates[];
  @Output() tabStatesChange: EventEmitter<TabStates[]> = new EventEmitter<TabStates[]>();

  @Input() tabIndex!: number;

  @ViewChild('stockMenu') stockMenu!: Menu;
  @ViewChild('stockPop') stockPop!: Popover;
  selectedStock!: Stock | null;
  detailTabIndex: number = 1;
  historiqueTabIndex: number = 5;
  diffusionTabIndex: number = 7;

  isLoadingData: boolean = true;
  isRecommendedPriceLoaded: boolean = false;
  isHistoriquePricesLoaded: boolean = false;
  isSendingData: boolean = false;
  isUpdatingStock: boolean = false;

  recommendedPrice: EbayProductAnalysis | null = null;
  historyPrices: StockPriceHistory[] = [];

  menuItems: MenuItem[] = [
    { label: 'Modifier', icon: PrimeIcons.PENCIL, command: () => this.showStockModal(this.selectedStock) },
    { label: 'Supprimer', icon: PrimeIcons.TRASH, iconClass: '!text-red-500 dark:!text-red-400', command: () => this.deleteStock(this.selectedStock) }
  ]

  qantitySubject = new Subject<{id: number, qte: number}>();

  constructor(
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private stockService: StockService,
    private ebayService: EbayService,
    private productService: ProductService,
    public utilityService: UtilityService
  ) {
    super();
    this.qantitySubject.pipe(debounceTime(600)).subscribe(value => this.updateStockQuantity(value));
  }

  ngOnInit(): void {
    this.getProductRecommendedPrice();
    this.getProductPricesHistoriques();
    this.getProductEbay();
    this.getProductStocks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabStates']) {
      const tab: TabStates = this.tabStates.find(state => state.index === this.tabIndex) as TabStates
      if (tab.active && !tab.loaded) {
        this.getProductEbay();
        this.getProductStocks();
      }
    }
  }

  getProductEbay(): void {
      this.productService
          .get(this.product.id, {fields: {produits: 'produits_ebay'}})
          .pipe(takeUntil(this.destroy$))
          .subscribe({
              next: data => {
                  this.product = { ...this.product, produits_ebay: data.produits_ebay };
              }
          })
  }

  getProductStocks(): void {
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.stockService
      .getCollection({ produit_id: this.product.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.product = { ...this.product, stocks: data.results };
          this.tabStates.map((state: TabStates) => {
            if (state.index === this.tabIndex)
              state.loaded = true
          });
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.productChange.emit(this.product)
          this.isLoadingData = false;

          const action = this.route.snapshot.queryParams['stock_id'];
          if (action && action != '') {
              this.location.replaceState(this.location.path(false).split('?')[0])
              const stock = this.product.stocks?.find(s => s.id === +action) || null;
              this.showStockModal(stock);
          }
        }
      });
  }

  changeStockQuantity(stock: Stock, qte: number): void {
    this.product.stocks?.map((s) => {
      if (s.id === stock.id) {
        s.quantite = (qte <= 0 ? 0 : qte)
      }
      return s
    })
    this.qantitySubject.next({id: stock.id, qte: qte})
  }

  updateStockQuantity(value: {id: number, qte: number}): void {
    this.isUpdatingStock = true
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.stockService
      .patch(value.id, {quantite: value.qte})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isUpdatingStock = false;
          this.getProductStocks();
          const historiqueTab: TabStates = this.tabStates.find(state => state.index === this.historiqueTabIndex) as TabStates
          if (historiqueTab.loaded){
            historiqueTab.loaded = false;
            this.tabStatesChange.emit(this.tabStates);
          }
          const diffusionTab: TabStates = this.tabStates.find(state => state.index === this.diffusionTabIndex) as TabStates
          if (diffusionTab.loaded) {
            diffusionTab.loaded = false;
            this.tabStatesChange.emit(this.tabStates);
          }
          if(value.qte <= 0) {
              const stock: Stock | undefined = this.product.stocks?.find(s => s.id === value.id);
              this.deleteStock(stock || null);
          }
        },
        error: () => {
          this.apiCall$?.unsubscribe();
          this.isUpdatingStock = false;
        }
      });
  }

  getProductRecommendedPrice(): void {
    this.isRecommendedPriceLoaded = false;
    this.ebayService
      .getEbayAnalyseMarche(this.product.id as string | number)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.recommendedPrice = data,
        complete: () => this.isRecommendedPriceLoaded = true
      });
  }

  getProductPricesHistoriques(): void {
    this.isHistoriquePricesLoaded = false;
    this.productService
      .getStockPriceHistory({ produit_id: this.product.id, page: 1, size: 20, order_by: 'id', order: 'desc' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.historyPrices = data.results,
        complete: () => this.isHistoriquePricesLoaded = true
      });
  }

  onStockMenuOpen(event: Event, stock: Stock): void {
    if (this.stockMenu) {
      this.selectedStock = stock;
      this.stockMenu.show(event)
    }
  }

  onStockPopOpen(event: Event, stock: Stock): void {
    if (this.stockPop) {
      this.selectedStock = stock;
      this.stockPop.toggle(event)
    }
  }

  navigateToEbay(stock?: Stock): void {
    if(stock?.url_ebay)
      window.open(stock.url_ebay, '_blank')
  }

  showChartModal(): void {
      const data:any = { product: this.product };
      this.dialogRef = this.dialogService.open(ProductStockChart, {
          inputValues: data,
          width: '800px',
          modal: true,
          focusOnShow: false,
          closable: true,
          closeOnEscape: true,
          dismissableMask: true,
          draggable: false,
          header: `Fluctuation du prix des stocks`
      });
  }

  showStockModal(stock: Stock | null): void {
    const data:any = { product: this.product, recommendedPrice: this.recommendedPrice };
    if (stock) {
      data.stock = stock;
      data.stock.secteur_id = stock.secteur?.id;
    }

    this.dialogRef = this.dialogService.open(ProductStockForm, {
      inputValues: data,
      width: '600px',
      modal: true,
      focusOnShow: false,
      header: `${false ? 'Modification de l\'' : 'Création d\'un'} stock`
    });

    this.dialogRef?.onClose.subscribe((data: Stock | null) => {
      // If stock created or updated, refresh ebay stocks tab
      if (data) {
        this.getProductStocks();
        this.getProductPricesHistoriques();
        const diffusionTab: TabStates = this.tabStates.find(state => state.index === this.diffusionTabIndex) as TabStates
        if (diffusionTab.loaded) {
          diffusionTab.loaded = false;
          this.tabStatesChange.emit(this.tabStates);
        }
        const historiqueTab: TabStates = this.tabStates.find(state => state.index === this.historiqueTabIndex) as TabStates
        if (historiqueTab.loaded){
            historiqueTab.loaded = false;
            this.tabStatesChange.emit(this.tabStates);
        }
      }
    });
  }

  deleteStock(stock: Stock | null): void {
    if (!stock) return;

    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir supprimer ce stock?',
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: PrimeIcons.CHECK,
      acceptButtonProps: {
        iconPos: 'right'
      },
      rejectIcon: PrimeIcons.TIMES,
      rejectButtonProps: {
        text: true,
        severity: 'danger'
      },
      accept: () => {
        this.stockService
          .delete(stock.id as string | number)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const stocks = this.product.stocks?.filter((s) => s.id != stock.id) as Stock[] || []
              this.product = { ...this.product, stocks: stocks };
              this.productChange.emit(this.product)
                const detailTab: TabStates = this.tabStates.find(state => state.index === this.detailTabIndex) as TabStates
                if (detailTab.loaded) {
                    detailTab.loaded = false;
                    this.tabStatesChange.emit(this.tabStates);
                }
            },
            complete: () => {
                this.toastService.add({ severity: 'success', summary: 'Succès', detail: 'Stock supprimé' })
                this.getProductPricesHistoriques();
            },
          });
      },
      reject: () => {
        if(stock.quantite !== null && stock.quantite <= 0) {
            this.changeStockQuantity(stock, (stock.quantite + 1));
        }
      }
    });
  }
}
